import { useTranslation } from 'react-i18next';

import { Input } from '@any-ui-react/core';
import { Menu, MenuItem, SubMenu } from '@szhsin/react-menu';
import cx from 'classnames';
import { tv } from 'tailwind-variants';

import { ChevronRightIcon } from '@anyx/shared-icons';
import { SelectButton, SlotsToClasses } from '@anyx/shared-ui';


import { useConditionBuilderStore } from '../../store';
import { Option } from '../../types/basic';

const styles = tv({
  slots: {
    root: 'w-full',
    menu: 'border-gray-2 w-80 border p-2 text-sm shadow-sm',
    menuItems: 'text-gray-9 hover:bg-gray-0 rounded-sm px-2 leading-8',
    wrapper: 'w-52',
  },
  variants: {
    subMenu: {
      true: {
        menuItems: 'flex items-center gap-x-2',
      },
    },
    compact: {
      true: {
        menu: 'w-full',
        wrapper: 'w-full',
      },
    },
  },
});

type ConditionSelectorSlots = keyof ReturnType<typeof styles>;

const SubMenuLabel = ({ label }: { label: string }) => {
  return (
    <>
      <span>{label}</span>
      <ChevronRightIcon className="text-gray-7 ml-auto h-4 w-4" />
    </>
  );
};

export interface ConditionSelectorProps {
  onSelect?: (option: string) => void;
  value?: string;
  error?: string;
  classNames?: SlotsToClasses<ConditionSelectorSlots>;
  disabled?: boolean;
}

export const ConditionSelector = ({
  onSelect,
  value,
  error,
  classNames,
  disabled = false,
}: ConditionSelectorProps) => {
  const { t } = useTranslation('crm');
  const fields = useConditionBuilderStore((state) => state.fields);
  const fieldMap = useConditionBuilderStore((state) => state.fieldMap);
  const compact = useConditionBuilderStore((state) => state.compact);

  const classes = styles({
    compact,
  });

  return (
    <Menu
      className={classes.root()}
      menuClassName={classes.menu()}
      menuButton={({ open }) => (
        <Input.Wrapper error={error} className={classes.wrapper()}>
          <SelectButton
            active={open}
            placeholder={t('crm.conditionBuilder.selector.placeholder')}
            value={value ? (fieldMap?.get(value)?.label ?? value) : undefined}
            error={error}
            disabled={disabled}
          />
        </Input.Wrapper>
      )}
      gap={4}
      onItemClick={(e) => {
        if (typeof e.value === 'string') onSelect?.(e.value);
      }}
    >
      {fields?.map((item) =>
        'options' in item ? (
          <SubMenu
            key={item.label}
            gap={8}
            itemProps={{ className: classes.menuItems({ subMenu: true }) }}
            menuClassName={classes.menuItems({
              subMenu: false,
              className: cx('w-max', classNames?.menu || ''),
            })}
            label={() => <SubMenuLabel label={item.label} />}
          >
            {item.options?.map((subItem: Option) => (
              <MenuItem
                key={subItem.value}
                className={classes.menuItems({
                  subMenu: false,
                  className: classNames?.menuItems,
                })}
                value={subItem.value}
              >
                {subItem.label}
              </MenuItem>
            ))}
          </SubMenu>
        ) : (
          <MenuItem
            key={item.label}
            className={classes.menuItems({ subMenu: true })}
            value={item.value}
          >
            {item.label}
          </MenuItem>
        )
      )}
    </Menu>
  );
};
